import React, { useEffect, useState, useRef } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import styles from "./QuickPosting.module.scss";
import { Dialog } from "@progress/kendo-react-dialogs";
import { getPostingTypeDetails } from "services/postingTypesService";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import MattersCombobox from "pages/Postings/MattersComboBox";
import { MatterDocPostingTypes } from "components/BatchPosting/Form";
import {
  validateAddReference,
  validateDate,
  ValidateNetAmount,
  ValidateVATAmount,
  validateDetails,
  validateCounselFee,
} from "components/BatchPosting/InputComponents";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import { useUploadForm } from "components/fileUploadHook";
import { onFileUpload } from "services/fileDataService";
import { FileDocumentTypes } from "components/Enums/FileDocumentTypes.ts";
import { DocumentsUpload } from "pages/Postings/DcoumentsUpload";
import Modal from "pages/Settings/Modal";
import * as api from "../../api/index";
import { PostingSubmitFilter } from "components/BatchPosting/SubmitFunction";
import { TransactionPeriod } from "components/common/TransactionPeriod";
import { getSelectedSessionPeriodNumber } from "services/transactionPeriodService";
import { toast } from "react-toastify";
import { useActiveVats } from "services/vatService";
import { useCompany } from "services/companyService";
import SubmitButton from "components/common/SubmitButton";

export default function QuickPostings({
  postingCode,
  onClose,
  onSubmit,
  defaultMatter,
  defaultValues
}) {
  const [fields, setFields] = useState([]);
  const [postingMeta, setPostingMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matterId, setMatterId] = useState(defaultMatter); // or passed as prop
  const [filesData, setFilesData] = useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [initialValues, setInitialValues] = useState({ date: new Date() });
  const [vatCode, setVatCode] = useState(null);
  const [documentUpload, setDocumentsUpload] = useState(false);
  const { uploadForm, progress } = useUploadForm();
  const [submitting, setSubmitting] = useState(false);
  const { data: company } = useCompany();
  const { data: activevatsdata = [] } = useActiveVats();

  const hasInitializedVat = useRef(false);

  const requiredValidator = (value) => (value ? "" : "This field is required.");

  useEffect(() => {
    if (
      defaultValues?.defaultVatCodeId && 
      activevatsdata.length > 0 && 
      !vatCode && 
      !hasInitializedVat.current
    ) {
      const defaultVat = activevatsdata.find(
        (item) => item.vatCodeId === defaultValues?.defaultVatCodeId
      );
      if (defaultVat) {
        setVatCode(defaultVat);
        hasInitializedVat.current = true;
      }
    }
  }, [activevatsdata]);

  // Fetch displayFields from API when postingCode changes
  useEffect(() => {
    const fetchPostingDetails = async () => {
      try {
        const response = await getPostingTypeDetails(postingCode);
        setFields(response?.displayFields || []);
        setPostingMeta(response);
      } catch (error) {
        console.error("Failed to fetch posting details", error);
        setFields([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPostingDetails();
  }, [postingCode]);

  const handleMatterChange = (e) => {
    if (MatterDocPostingTypes.includes(postingMeta?.code) && filesData.length) {
      setErrorModal(true);
      return;
    }
    // onChangematter(e);
    setMatterId(e?.value);
  };

  const isFieldVisible = (name) => {
    const field = fields.find((f) => f.name === name);
    return field && !field.hide;
  };

  const handleUpload = async (e) => {
    const data = onFileUpload(e);
    setFilesData([...filesData, data]);

    const parameters = {
      documentType: FileDocumentTypes.Posting,
      matterId: matterId?.matterId,
    };

    const dataChoosen = await uploadForm(data, e.target.files[0], parameters);
    data.fileID = dataChoosen?.data?.data?.fileID;
  };

  const onFileRemove = (fileId) => {
    setFilesData((prev) => prev.filter((file) => file.fileID !== fileId));
  };

  // Handle form submission
  const handleFormSubmit = async (values) => {
    try {
      setSubmitting(true); // 🔁 instead of setLoading(true)

      const payload = {
        ...values,
        postingType: { code: postingMeta?.code },
        documentFiles: filesData,
        matterID: matterId,
        vatCode: vatCode,
      };

      const postingPayload = [payload];
      const filteredData = PostingSubmitFilter(postingPayload, company);

      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        toast.error("Posting data is invalid or empty.");
        return;
      }

      if (filteredData[0].vatAmount == null) {
        filteredData[0].vatAmount = 0;
      }

      filteredData[0].transactionPeriod = getSelectedSessionPeriodNumber();

      const response = await api.createposting(filteredData);
      const result = response?.data?.data?.[0];

      if (result) {
        toast.success(
          `Posted Successfully for ${result.postingType} - ${result.transactionNo}`
        );
      } else {
        toast.error("Posted but response data was missing.");
      }

      onClose?.();
      onSubmit?.({
        ...filteredData[0],
        ...result,
      });
    } catch (error) {
      console.error("Posting error:", error);
    } finally {
      setSubmitting(false); // 🔁 instead of setLoading(false)
    }
  };

  return (
    <Dialog
      title={postingMeta ? `Add ${postingMeta?.name}` : "Loading..."}
      onClose={() => onClose()}
      width={500}
      className="p-3"
    >
      <div
        className={`dialog-content p-3 py-0 ${
          loading && "d-flex align-items-center justify-content-center"
        }`}
        style={{ minHeight: 540 }}
      >
        {loading ? (
          <div className="spinner-border spinner" role="status" />
        ) : (
          <div className="h-100 d-flex justify-content-center">
            <div className={`bgForm overflow-auto ${styles["main-container"]}`}>
              <Form
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                render={(formRenderProps) => (
                  <FormElement noValidate>
                    <div>
                      <TransactionPeriod
                        showReload={false}
                        customStyles={"justify-content-between w-100 mb-2"}
                      />
                    </div>
                    {isFieldVisible("MatterID") && (
                      <div className="w-100 mt-3 mb-2">
                        <Label className="label">Matter</Label>
                        <MattersCombobox
                          className="w-100"
                          name="matterIDFrom"
                          value={matterId}
                          disabled={defaultMatter}
                          onChangematter={(e) => {
                            if (e.value === null || !e?.value?.feeEarnerRef) {
                              formRenderProps.onChange("feeEarnerRef", {
                                value: null,
                              });
                            } else {
                              formRenderProps.onChange("feeEarnerRef", {
                                value: {
                                  userRef: e?.value?.feeEarnerRef,
                                  representation: `${e?.value?.feeEarnerRef} - ${e.value?.feeEarnerName}`,
                                },
                              });
                            }
                            handleMatterChange(e);
                          }}
                          setErrorModal={setErrorModal}
                          boxShadow={false}
                        />
                      </div>
                    )}

                    <div className="d-flex mb-2">
                      {isFieldVisible("Date") && (
                        <div style={{ width: "150px", marginRight: "15px" }}>
                          <Field
                            name="date"
                            component={validateDate}
                            boxShadow={false}
                          />
                        </div>
                      )}
                      {isFieldVisible("Reference") && (
                        <div>
                          <Field
                            validator={requiredValidator}
                            name="reference"
                            component={validateAddReference}
                            boxShadow={false}
                          />
                        </div>
                      )}
                    </div>

                    {isFieldVisible("NetAmount") && (
                      <div className="mb-2">
                        <Field
                          validator={requiredValidator}
                          name="netAmount"
                          component={ValidateNetAmount}
                          formRenderProps={formRenderProps}
                          boxShadow={false}
                          NetAmount={formRenderProps.valueGetter("netAmount")}
                          VatCode={vatCode}
                        />
                      </div>
                    )}
                    {isFieldVisible("VatCode") && (
                      <div className="d-flex mb-2">
                        <div style={{ width: "160px", marginRight: "10px" }}>
                          <SearchComboBox
                            name="vatCode"
                            cbType={CONSTANT.AVATS}
                            width="auto"
                            customCombo={true}
                            required={true}
                            boxShadow={false}
                            value={vatCode}
                            onChangeFunction={(e) => {
                              const vatRate = e?.value?.rate || 0;
                              const netAmount =
                                formRenderProps.valueGetter("netAmount");
                              const calculatedVat = netAmount * (vatRate / 100);
                              formRenderProps.onChange("vatAmount", {
                                value: !isNaN(calculatedVat)
                                  ? calculatedVat
                                  : 0.0,
                              });
                              setVatCode(e.value);
                            }}
                            defaultValueSetter={formRenderProps}
                          />
                        </div>

                        <div style={{ width: "156px" }}>
                          <Field
                            name="vatAmount"
                            component={ValidateVATAmount}
                            NetAmount={formRenderProps.valueGetter("netAmount")}
                            VatCode={formRenderProps.valueGetter("vatCode")}
                            boxShadow={false}
                          />
                        </div>
                      </div>
                    )}

                    {isFieldVisible("FeeEarnerRef") && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="feeEarnerRef"
                          cbType={CONSTANT.FE}
                          width="auto"
                          boxShadow={false}
                          defaultValues={defaultValues}
                          defaultValueSetter={formRenderProps}
                        />
                      </div>
                    )}
                    {isFieldVisible("Details") && (
                      <div className="mb-2">
                        <Field
                          validator={requiredValidator}
                          name="details"
                          component={validateDetails}
                          boxShadow={false}
                        />
                      </div>
                    )}

                    <div className="d-flex justify-content-between w-100 align-items-center mb-3">
                      {isFieldVisible("IsCounselFee") && (
                        <div className="">
                          <Field
                            name="isCounselFee"
                            component={validateCounselFee}
                          />
                        </div>
                      )}
                      <div>
                        <DocumentsUpload
                          position="static"
                          open={documentUpload}
                          value={postingMeta}
                          type={"All Postings"}
                          reset={false}
                          progress={progress}
                          filesData={filesData}
                          onFileRemove={onFileRemove}
                          onError={() => {
                            const newFiles = filesData.splice(
                              filesData.length,
                              1
                            );
                            setFilesData([...newFiles]);
                          }}
                          onUpload={handleUpload}
                          disabled={false}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      id="quick-posting-form"
                      className="common-btn border-0 d-none"
                    ></button>
                  </FormElement>
                )}
              />
            </div>
          </div>
        )}
        <Modal
          value={errorModal}
          error={true}
          reset={() => setErrorModal(false)}
          onCancel={() => setErrorModal(false)}
          component={"Posting"}
          display={
            "You have unposted files! By changing posting type you will lose files!"
          }
          clearFile={() => setFilesData([])}
        />
      </div>
      <div className="dialog-footer">
        <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton
          className="common-btn me-3"
          disabled={submitting}
          onClick={() => {
            if (!submitting) {
              document.getElementById("quick-posting-form")?.click();
            }
          }}
          isLoading={submitting}
        >
          Add to Bill
        </SubmitButton>
      </div>
    </Dialog>
  );
}
